import { LimitKeys, Timestamp, User } from '@griply/types';
import { isAfter, isSameDay } from 'date-fns';

import { unlimitedLimit } from './get-fallback-limits';

export type LimitCheckFunctionParams = {
  [LimitKeys.GoalUserPhoto]: { user: User; today: Timestamp };
  [LimitKeys.CreateGoal]: {
    totalUncompleted: number;
    user: User;
    today: Timestamp;
  };
  [LimitKeys.SubGoals]: {
    totalUncompleted: number;
    user: User;
    today: Timestamp;
  };
  [LimitKeys.CreateHabit]: { total: number; user: User; today: Timestamp };
  [LimitKeys.HabitIcons]: { user: User; today: Timestamp };
  [LimitKeys.HabitScheduleTarget]: { user: User; today: Timestamp };
  [LimitKeys.CreateSmartList]: { total: number; user: User; today: Timestamp };
  [LimitKeys.BiometricAppLock]: { user: User; today: Timestamp };
  [LimitKeys.CustomLifeAreas]: {
    totalCustom: number;
    user: User;
    today: Timestamp;
  };
  [LimitKeys.CustomizeDefaultLifeAreas]: { user: User; today: Timestamp };
  [LimitKeys.EditLifeAreas]: { user: User; today: Timestamp };
  [LimitKeys.VisionUserPhoto]: { user: User; today: Timestamp };
  [LimitKeys.Insights]: { user: User; today: Timestamp };
  [LimitKeys.DesktopCalendarDay]: { user: User; today: Timestamp };
  [LimitKeys.DesktopCalendarWeek]: { user: User; today: Timestamp };
  [LimitKeys.DesktopCalendarFourDays]: { user: User; today: Timestamp };
  [LimitKeys.DesktopCalendarMonth]: { user: User; today: Timestamp };
  [LimitKeys.DesktopGoalsOverviewTimeline]: { user: User; today: Timestamp };
  [LimitKeys.MobileGoalsOverviewTimeline]: { user: User; today: Timestamp };
  [LimitKeys.MobileCalendar1DayView]: { user: User; today: Timestamp };
  [LimitKeys.MobileCalendar2DayView]: { user: User; today: Timestamp };
  [LimitKeys.MobileCalendar3DayView]: { user: User; today: Timestamp };
  [LimitKeys.MobileCalendarWeekView]: { user: User; today: Timestamp };
  [LimitKeys.TwoWayCalendarSync]: { user: User; today: Timestamp };
};

const isEnforcedOnOrBefore = (
  enforcedAt: Timestamp | null | undefined,
  today: Timestamp,
): boolean => {
  if (!enforcedAt) {
    return false;
  }
  return !isAfter(enforcedAt, today) || isSameDay(enforcedAt, today);
};

const goalUserPhoto = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.GoalUserPhoto]): boolean =>
  user.limits[LimitKeys.GoalUserPhoto].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(user.limits[LimitKeys.GoalUserPhoto].enforcedAt, today);

const createGoal = ({
  user,
  today,
  totalUncompleted,
}: LimitCheckFunctionParams[LimitKeys.CreateGoal]): boolean =>
  user.limits[LimitKeys.CreateGoal].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(user.limits[LimitKeys.CreateGoal].enforcedAt, today) ||
  totalUncompleted < user.limits[LimitKeys.CreateGoal].limit;

const subGoals = ({
  user,
  today,
  totalUncompleted,
}: LimitCheckFunctionParams[LimitKeys.SubGoals]): boolean =>
  user.limits[LimitKeys.SubGoals].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(user.limits[LimitKeys.SubGoals].enforcedAt, today) ||
  totalUncompleted < user.limits[LimitKeys.SubGoals].limit;

const createHabit = ({
  user,
  today,
  total,
}: LimitCheckFunctionParams[LimitKeys.CreateHabit]): boolean =>
  user.limits[LimitKeys.CreateHabit].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(user.limits[LimitKeys.CreateHabit].enforcedAt, today) ||
  total < user.limits[LimitKeys.CreateHabit].limit;

const habitIcons = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.HabitIcons]): boolean =>
  user.limits[LimitKeys.HabitIcons].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(user.limits[LimitKeys.HabitIcons].enforcedAt, today);

const habitScheduleTarget = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.HabitScheduleTarget]): boolean =>
  user.limits[LimitKeys.HabitScheduleTarget].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.HabitScheduleTarget].enforcedAt,
    today,
  );

const createSmartList = ({
  total,
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.CreateSmartList]): boolean =>
  user.limits[LimitKeys.CreateSmartList].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.CreateSmartList].enforcedAt,
    today,
  ) ||
  total < user.limits[LimitKeys.CreateSmartList].limit;

const biometricAppLock = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.BiometricAppLock]): boolean =>
  user.limits[LimitKeys.BiometricAppLock].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.BiometricAppLock].enforcedAt,
    today,
  );

const customLifeAreas = ({
  user,
  today,
  totalCustom,
}: LimitCheckFunctionParams[LimitKeys.CustomLifeAreas]): boolean =>
  user.limits[LimitKeys.CustomLifeAreas].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.CustomLifeAreas].enforcedAt,
    today,
  ) ||
  totalCustom < user.limits[LimitKeys.CustomLifeAreas].limit;

const customizeDefaultLifeAreas = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.CustomizeDefaultLifeAreas]): boolean =>
  user.limits[LimitKeys.CustomizeDefaultLifeAreas].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.CustomizeDefaultLifeAreas].enforcedAt,
    today,
  );

const editLifeAreas = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.EditLifeAreas]): boolean =>
  user.limits[LimitKeys.EditLifeAreas].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(user.limits[LimitKeys.EditLifeAreas].enforcedAt, today);

const visionUserPhoto = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.VisionUserPhoto]): boolean =>
  user.limits[LimitKeys.VisionUserPhoto].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.VisionUserPhoto].enforcedAt,
    today,
  );

const insights = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.Insights]): boolean =>
  user.limits[LimitKeys.Insights].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(user.limits[LimitKeys.Insights].enforcedAt, today);

const desktopCalendarDay = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.DesktopCalendarDay]): boolean =>
  user.limits[LimitKeys.DesktopCalendarDay].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.DesktopCalendarDay].enforcedAt,
    today,
  );

const desktopCalendarWeek = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.DesktopCalendarWeek]): boolean =>
  user.limits[LimitKeys.DesktopCalendarWeek].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.DesktopCalendarWeek].enforcedAt,
    today,
  );

const desktopCalendarFourDays = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.DesktopCalendarFourDays]): boolean =>
  user.limits[LimitKeys.DesktopCalendarFourDays].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.DesktopCalendarFourDays].enforcedAt,
    today,
  );

const desktopCalendarMonth = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.DesktopCalendarMonth]): boolean =>
  user.limits[LimitKeys.DesktopCalendarMonth].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.DesktopCalendarMonth].enforcedAt,
    today,
  );

const desktopGoalsOverviewTimeline = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.DesktopGoalsOverviewTimeline]): boolean =>
  user.limits[LimitKeys.DesktopGoalsOverviewTimeline].limit ===
    unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.DesktopGoalsOverviewTimeline].enforcedAt,
    today,
  );

const mobileGoalsOverviewTimeline = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.MobileGoalsOverviewTimeline]): boolean =>
  user.limits[LimitKeys.MobileGoalsOverviewTimeline].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.MobileGoalsOverviewTimeline].enforcedAt,
    today,
  );

const mobileCalendar1Day = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.MobileCalendar1DayView]): boolean =>
  user.limits[LimitKeys.MobileCalendar1DayView].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.MobileCalendar1DayView].enforcedAt,
    today,
  );

const mobileCalendar2Day = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.MobileCalendar2DayView]): boolean =>
  user.limits[LimitKeys.MobileCalendar2DayView].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.MobileCalendar2DayView].enforcedAt,
    today,
  );

const mobileCalendar3Day = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.MobileCalendar3DayView]): boolean =>
  user.limits[LimitKeys.MobileCalendar3DayView].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.MobileCalendar3DayView].enforcedAt,
    today,
  );

const mobileCalendarWeek = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.MobileCalendarWeekView]): boolean =>
  user.limits[LimitKeys.MobileCalendarWeekView].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.MobileCalendarWeekView].enforcedAt,
    today,
  );

const twoWayCalendarSync = ({
  user,
  today,
}: LimitCheckFunctionParams[LimitKeys.TwoWayCalendarSync]): boolean =>
  user.limits[LimitKeys.TwoWayCalendarSync].limit === unlimitedLimit ||
  !isEnforcedOnOrBefore(
    user.limits[LimitKeys.TwoWayCalendarSync].enforcedAt,
    today,
  );

export const limitKeyFunctionMap = {
  [LimitKeys.GoalUserPhoto]: goalUserPhoto,
  [LimitKeys.CreateGoal]: createGoal,
  [LimitKeys.SubGoals]: subGoals,
  [LimitKeys.CreateHabit]: createHabit,
  [LimitKeys.HabitIcons]: habitIcons,
  [LimitKeys.HabitScheduleTarget]: habitScheduleTarget,
  [LimitKeys.CreateSmartList]: createSmartList,
  [LimitKeys.BiometricAppLock]: biometricAppLock,
  [LimitKeys.CustomLifeAreas]: customLifeAreas,
  [LimitKeys.CustomizeDefaultLifeAreas]: customizeDefaultLifeAreas,
  [LimitKeys.EditLifeAreas]: editLifeAreas,
  [LimitKeys.VisionUserPhoto]: visionUserPhoto,
  [LimitKeys.Insights]: insights,
  [LimitKeys.DesktopCalendarDay]: desktopCalendarDay,
  [LimitKeys.DesktopCalendarWeek]: desktopCalendarWeek,
  [LimitKeys.DesktopCalendarFourDays]: desktopCalendarFourDays,
  [LimitKeys.DesktopCalendarMonth]: desktopCalendarMonth,
  [LimitKeys.DesktopGoalsOverviewTimeline]: desktopGoalsOverviewTimeline,
  [LimitKeys.MobileGoalsOverviewTimeline]: mobileGoalsOverviewTimeline,
  [LimitKeys.MobileCalendar1DayView]: mobileCalendar1Day,
  [LimitKeys.MobileCalendar2DayView]: mobileCalendar2Day,
  [LimitKeys.MobileCalendar3DayView]: mobileCalendar3Day,
  [LimitKeys.MobileCalendarWeekView]: mobileCalendarWeek,
  [LimitKeys.TwoWayCalendarSync]: twoWayCalendarSync,
};

export const isPremiumFeatureAllowed = <LimitKey extends LimitKeys>(
  limitKey: LimitKey,
  params: LimitCheckFunctionParams[LimitKey],
): boolean => {
  const checkFunction = limitKeyFunctionMap[limitKey] as (
    options: LimitCheckFunctionParams[LimitKey],
  ) => boolean;
  return checkFunction(params);
};
